// The "Tasks" kind of the import wizard (M2-08): the board the rows land on, the column mapping
// (task attributes and the board's own fields ← the file's columns, suggested from the header
// with ClickUp's export names understood) and a preview of the first rows as they map. The dry
// run and commit are the wizard's (`POST …/boards/{id}/tasks/import`, `mapping` part). Reads:
// the boards list, and the chosen board's fields.
import {
  importableField,
  normalizeHeader,
  type previewCsv,
  suggestTaskMapping,
  TASK_IMPORT_TARGETS,
  TASK_TARGET_LABELS,
  type TaskImportMapping,
  type TaskImportTarget,
} from "@companyos/domain-work/import";
import {
  Field,
  FieldDescription,
  FieldLabel,
  NativeSelect,
  NativeSelectOption,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import type { Api } from "../../api/client";
import { boardsQuery, fieldsQuery } from "../work/work-queries";

export type CsvPreview = ReturnType<typeof previewCsv>;

/** Header, example row and hint of the tasks template (ClickUp's export column names). */
export const TASKS_TEMPLATE = {
  label: "Tasks",
  header: "Task ID,Task Name,Status,Priority,Due Date,Assignees,Task Content",
  example:
    "CU-101,Call the candidate,To do,high,2026-11-02,Recruiting coordinator (DE),Confirm the start date",
  hint: "Tasks onto one board: map the file's columns to task fields. ClickUp's CSV export maps itself.",
};

const NONE = "";

/** Ready to dry-run: a board and a title column. */
export const taskSetupReady = (board: string, mapping: TaskImportMapping | null) =>
  board !== "" && !!mapping?.columns.title;

export function TaskImportPanel({
  api,
  co,
  preview,
  board,
  onBoard,
  mapping,
  onMapping,
}: {
  api: Api;
  co: string;
  preview: CsvPreview | null;
  board: string;
  onBoard: (id: string) => void;
  mapping: TaskImportMapping | null;
  onMapping: (m: TaskImportMapping) => void;
}) {
  const boards = useQuery(boardsQuery(api, co));
  const fields = useQuery({ ...fieldsQuery(api, co, board), enabled: board !== "" });
  const live = (boards.data?.data ?? []).filter((b) => b.archived_at === null);
  const defs = (fields.data?.data ?? []).filter(importableField);
  const header = preview?.header ?? [];
  const headerKey = header.join("\u0000");
  const fieldsKey = defs.map((f) => f.id).join(",");

  // A suggestion whenever the file or the board's fields change (the user then adjusts it).
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the header and field ids
  useEffect(() => {
    if (header.length > 0) onMapping(suggestTaskMapping(header, defs));
  }, [headerKey, fieldsKey]);

  const columnSelect = (
    id: string,
    label: string,
    value: string | undefined,
    set: (col: string | undefined) => void,
  ) => (
    <Field key={id}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <NativeSelect
        id={id}
        value={value ?? NONE}
        disabled={header.length === 0}
        onChange={(e) => set(e.target.value === NONE ? undefined : e.target.value)}
      >
        <NativeSelectOption value={NONE}>Not imported</NativeSelectOption>
        {header.map((h) => (
          <NativeSelectOption key={h} value={normalizeHeader(h)}>
            {h}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </Field>
  );

  const m: TaskImportMapping = mapping ?? { columns: {} };
  const setTarget = (t: TaskImportTarget, col: string | undefined) => {
    const columns = { ...m.columns };
    if (col === undefined) delete columns[t];
    else columns[t] = col;
    onMapping({ ...m, columns });
  };
  const setField = (id: string, col: string | undefined) => {
    const next = { ...(m.fields ?? {}) };
    if (col === undefined) delete next[id];
    else next[id] = col;
    onMapping(Object.keys(next).length > 0 ? { ...m, fields: next } : { columns: m.columns });
  };
  const cell = (row: Record<string, string>, t: TaskImportTarget) => {
    const c = m.columns[t];
    return c === undefined ? "—" : row[normalizeHeader(c)] || "—";
  };
  const shown: TaskImportTarget[] = ["title", "status", "priority", "due_at", "assignees"];

  return (
    <div className="mt-5 flex min-w-0 flex-col gap-5" data-slot="task-import">
      <Field>
        <FieldLabel htmlFor="import-board">Board</FieldLabel>
        <NativeSelect id="import-board" value={board} onChange={(e) => onBoard(e.target.value)}>
          <NativeSelectOption value="">Choose a board</NativeSelectOption>
          {live.map((b) => (
            <NativeSelectOption key={b.id} value={b.id}>
              {b.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <FieldDescription>
          {boards.isError
            ? "Boards could not be loaded."
            : "The tasks are created on this board; you need to be able to edit it."}
        </FieldDescription>
      </Field>

      <fieldset
        className="grid min-w-0 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        aria-label="Column mapping"
      >
        {TASK_IMPORT_TARGETS.map((t) =>
          columnSelect(
            `map-${t}`,
            t === "title" ? `${TASK_TARGET_LABELS[t]} (required)` : TASK_TARGET_LABELS[t],
            m.columns[t],
            (col) => setTarget(t, col),
          ),
        )}
        {defs.map((f) =>
          columnSelect(`map-field-${f.id}`, `Field: ${f.name}`, m.fields?.[f.id], (col) =>
            setField(f.id, col),
          ),
        )}
      </fieldset>

      {preview && preview.rows.length > 0 ? (
        <Table data-slot="task-preview">
          <TableCaption>
            The first {preview.rows.length} of {preview.total} row{preview.total === 1 ? "" : "s"},
            as mapped. The dry run checks every row against the board.
          </TableCaption>
          <TableHeader>
            <TableRow>
              {shown.map((t) => (
                <TableHead key={t} scope="col">
                  {TASK_TARGET_LABELS[t]}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {preview.rows.map((row, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: preview rows have no identity
              <TableRow key={i}>
                {shown.map((t) => (
                  <TableCell key={t}>{cell(row, t)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </div>
  );
}
